import { MapViewer } from '@3ddv/dvm-internal';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { ModalsService } from '@core/modals/modals.service';
import { Seat } from '@core/models';
import {
  AvailabilityService,
  CartService,
  ConfigurationService,
  DvmService,
  TutorialService,
  UtilitiesService,
} from '@core/services';
import { ANALYTICS_EVENTS, AnalyticsService } from '@core/services/analytics';
import { AdobeDataLayerService } from '@core/services/adobe-data-layer-service.service';
import { buildCartItems } from '@features/pages/select-seat/_utils/cart-items.builder';
import { UpgradesService } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.service';
import { Subscription } from 'rxjs';
import { IsolatedSeatsService } from './isolated-seats.service';

export type CombinedIds = {
  original_id: string;
  id: string;
};

@Injectable({
  providedIn: 'root',
})
export class SeatManagementService implements OnDestroy {
  private availabilityService: AvailabilityService =
    inject(AvailabilityService);
  private cartService: CartService = inject(CartService);
  private configurationService: ConfigurationService =
    inject(ConfigurationService);
  private dvmService: DvmService = inject(DvmService);
  private isolatedSeatsService: IsolatedSeatsService =
    inject(IsolatedSeatsService);
  private tutorialService: TutorialService = inject(TutorialService);
  private utilitiesService: UtilitiesService = inject(UtilitiesService);
  private modalsService: ModalsService = inject(ModalsService);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private adobeDataLayerService: AdobeDataLayerService = inject(
    AdobeDataLayerService
  );
  private upgradesService: UpgradesService = inject(UpgradesService);
  needADA: boolean = false;
  subscriptions: Subscription[] = [];
  viewer: MapViewer;
  private cartSelectedSectionIds: string[] = [];

  /**
   * Initializes the service and subscribes to tutorial events.
   * Handles specific tutorial steps related to seat selection and cleanup.
   */
  constructor() {
    const tutorial$ = this.tutorialService.tutorial$.subscribe(
      tutorialConfig => {
        if (tutorialConfig.activeStepCommand === 'setSeatsInCart') {
          const selectedSeatNodes =
            this.tutorialService.getTutorialSelectedSeatNodes();

          // Synthetic: the tutorial pre-fills the cart to demonstrate the flow, it is not
          // user intent, must not fire add_to_cart (plan §3 "Synthetic mutations").
          this.selectAvailableSeats(
            selectedSeatNodes.map(node => ({
              id: node.id,
              original_id: node.original_id,
            })),
            true
          );
        }

        if (tutorialConfig.isTutorialEnded) {
          // Synthetic: undoes the tutorial's own synthetic add above, not a user removal.
          this.unselectAllSelectedSeats(true);
        }
      }
    );
    this.dvmService.viewerSubscriptions.push(tutorial$);
  }

  /**
   * Cleans up subscriptions when the service is destroyed.
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Selects one or more available seats on the viewer and adds them to the cart.
   * If multiple seats are being selected at once (e.g., best available, area selection),
   * it first clears any previously selected seats.
   * @param nodeIds - An array of seat IDs (CombinedIds format) to select.
   * @param isSynthetic - true for a non-user-intent mutation (tutorial pre-fill, cart
   * restore after a bounce) — forwarded to selectSeatsForCart so it skips add_to_cart.
   */
  selectAvailableSeats(nodeIds: CombinedIds[], isSynthetic: boolean = false) {
    // SelectedSeats$ subscribers (upgrades threshold checks) can't see the isSynthetic
    // flag themselves — mark the emission window so a cart restore / tutorial pre-fill
    // doesn't count as a user-intent upgrade crossing.
    if (isSynthetic) {
      this.upgradesService.beginSyntheticCartEmission();
    }
    try {
      // Reset selectedSeats if you are adding a group of seats (best available or area selection)
      if (nodeIds.length > 1) {
        this.dvmService.viewerService.unselectAll();
        this.cartService.selectedSeats = {};
        this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
      }

      let ids = nodeIds.map(node => node.id);
      // Select Seat(s) on Viewer
      this.dvmService.viewerService.select(ids);

      this.selectSeatsForCart(nodeIds, isSynthetic);
    } finally {
      if (isSynthetic) {
        this.upgradesService.endSyntheticCartEmission();
      }
    }
  }

  /**
   * Unselects specified seats on the viewer, removes them from the cart,
   * recalculates cart totals, and updates the cart data.
   * @param nodeIds - An array of seat node objects (containing at least 'id') to unselect.
   */
  unselectSelectedSeats(
    nodeIds: { id: string; original_id?: string; originalId?: string }[]
  ) {
    // Unselect Seat on Viewer
    let success = this.dvmService.viewerService.unselect(nodeIds);

    const removedSeats: Seat[] = [];
    for (const nodeId of nodeIds) {
      const removedSeat = this.cartService.selectedSeats[nodeId.id];
      if (removedSeat) {
        removedSeats.push(removedSeat);
      }
      // Remove one new seat (in Cart Service)
      let isDeleted = delete this.cartService.selectedSeats[nodeId.id];
      this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    }

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    // Update cart Seats data
    this.updateCartSeatsBySection();

    this.trackRemoveFromCart(removedSeats);
  }

  /**
   * Unselects all currently selected seats on the viewer, clears the cart completely,
   * resets cart totals and data, and navigates back to the top view if currently in a seat view.
   * This is typically used for actions like clearing the entire selection or starting over.
   */
  unselectAllSelectedSeats(isSynthetic: boolean = false) {
    const removedSeats = Object.values(this.cartService.selectedSeats);
    let success = this.dvmService.viewerService.unselectAll();

    // Reset new seats (in Transaction Service)
    this.cartService.selectedSeats = {};
    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    // Update cart Seats data
    this.updateCartSeatsBySection();

    this.cartService.resetCart();

    // If the user is currently zoomed into a section (not in top view), navigate back to the top view.
    if (!this.dvmService.isTopView()) {
      const subscription = this.dvmService.openSectionMap().subscribe();
      this.subscriptions.push(subscription);
    }

    if (!isSynthetic) {
      this.trackRemoveFromCart(removedSeats);
    }
  }

  /**
   * Unselects all currently selected seats on the viewer and clears the cart,
   * similar to `unselectAllSelectedSeats`, but *without* triggering navigation
   * back to the top view. Useful for internal cleanup where map navigation is not desired.
   */
  unselectAllSelectedSeatsWithoutLoadingMap() {
    const removedSeats = Object.values(this.cartService.selectedSeats);
    const selectedSeats = this.dvmService.viewerService.getNodes({
      type: ['seat'],
      state: 'selected',
    });
    this.dvmService.viewerService.unselect(selectedSeats);
    // Reset new seats (in Transaction Service)
    this.cartService.selectedSeats = {};
    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    // Update cart Seats data
    this.updateCartSeatsBySection();

    // Always real user intent (best-available.service re-picking a new set) — no synthetic
    // caller of this variant exists today.
    this.trackRemoveFromCart(removedSeats);
  }

  /**
   * Adds detailed information for the specified seats to the cart service.
   * It fetches pricing details from the AvailabilityService for each seat,
   * updates the cart's selected seats map, recalculates totals, and updates
   * the section-based grouping of seats.
   * @param nodeIds - An array of seat IDs (either CombinedIds format or simple strings) to add to the cart.
   * @param isSynthetic - true for a non-user-intent mutation (tutorial pre-fill, cart
   * restore after a bounce) — skips add_to_cart, since it is not user intent.
   */
  selectSeatsForCart(
    nodeIds: CombinedIds[] | string[],
    isSynthetic: boolean = false
  ) {
    const unavailableSeatIds: string[] = [];
    const addedSeats: Seat[] = [];

    // Add seats to Cart
    for (const node of nodeIds) {
      // Merging new seat info from two objects:
      const newSeat = this.utilitiesService.splittedSeatInfoFromId(
        node
      ) as Seat;
      const id = typeof node === 'string' ? node : node.id;

      // Fetch detailed seat info, including price tiers, from the availability data.
      const seatInfo = this.availabilityService.getSeatByIdFromAvailability(id);

      // The seat has no availability entry (e.g. excluded because the event's default buyer
      // type has no price point for it) - it must never be added to the cart with an undefined
      // price, so skip it entirely.
      if (!seatInfo) {
        // Diagnostic context for the "Seat(s) no longer available" modal: when this
        // fires during the tutorial, it means `AvailabilityService.seatsAvailability`
        // doesn't hold this seat under the section bucket the tutorial expects — the
        // sectionId derived from the seat id, and the buckets actually loaded, tell us
        // whether it's a wrong-bucket-key issue or a genuinely empty section.
        console.error(
          `Seat ${id} has no availability entry - skipping cart selection`,
          {
            isTutorialOpen: this.tutorialService.isOpen(),
            derivedSectionId: id.split('-')[0],
            loadedSectionBuckets: Object.keys(
              this.availabilityService.seatsAvailability
            ),
          }
        );
        unavailableSeatIds.push(id);
        continue;
      }

      newSeat.price = seatInfo?.price?.[0]?.value; // The first price is the default buyer type price.
      newSeat.totalFee = seatInfo?.price?.[0]?.totalFee; // The first price is the default buyer type price.
      newSeat.totalPriceWithFees = seatInfo?.price?.[0]?.totalPriceWithFees; // The first price is the default buyer type price.
      // Add one new seat (in Cart Service)
      this.cartService.selectedSeats[newSeat.id] = newSeat;
      addedSeats.push(newSeat);
      // Notify subscribers about the change in selected seats.
      this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    }

    // Totals must be recomputed BEFORE updateCartSeatsBySection() below: it fires the final
    // selectedSeats$ emission, and cart_abandoned's arming subscriber (armCartAbandon in
    // select-seat.component.ts) reads selectedSeatsTotalPrice synchronously off that emission
    // — a stale total here means a stale cart_abandoned value. Mirrors unselectSelectedSeats().
    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    // Update cart Seats data
    this.updateCartSeatsBySection();

    if (unavailableSeatIds.length > 0) {
      // These seats were already marked "selected" on the map undo that so the map
      // doesn't show them as reserved when they were never actually added to the cart.
      this.dvmService.viewerService.unselect(
        unavailableSeatIds.map(id => ({ id }))
      );

      this.modalsService.openModal({
        title: 'Seat(s) no longer available',
        content: `The following seat(s) are no longer available and were not added to your selection: ${unavailableSeatIds.join(', ')}.`,
        acceptBtnName: 'CLOSE',
      });

      this.analyticsService.trackError({
        eventName: ANALYTICS_EVENTS.SEAT_SELECTION_ERROR,
        errorType: 'seats_unavailable',
        errorMessage: `Seat(s) no longer available: ${unavailableSeatIds.join(', ')}`,
        gameId: this.adobeDataLayerService.getEventId() || undefined,
      });
    }

    if (!isSynthetic) {
      this.trackAddToCart(addedSeats);
    }
  }

  /** Fires add_to_cart for a real (non-synthetic) selection, one row per section added. */
  private trackAddToCart(addedSeats: Seat[]): void {
    if (addedSeats.length === 0) {
      return;
    }

    const value = addedSeats.reduce(
      (subtotal, seat) => subtotal + (seat.price ?? 0),
      0
    );
    const seatCount = Object.keys(this.cartService.selectedSeats).length;

    this.analyticsService.trackAddToCart({
      currency: 'USD',
      value,
      items: this.buildAnalyticsItems(addedSeats),
      gameId: this.adobeDataLayerService.getEventId() || undefined,
      seatCount,
    });
  }

  /** Fires remove_from_cart for a real (non-synthetic) removal, one row per section removed. */
  private trackRemoveFromCart(removedSeats: Seat[]): void {
    if (removedSeats.length === 0) {
      return;
    }

    const value = removedSeats.reduce(
      (subtotal, seat) => subtotal + (seat.price ?? 0),
      0
    );
    const seatCount = Object.keys(this.cartService.selectedSeats).length;

    this.analyticsService.trackRemoveFromCart({
      currency: 'USD',
      value,
      items: this.buildAnalyticsItems(removedSeats),
      gameId: this.adobeDataLayerService.getEventId() || undefined,
      seatCount,
    });
  }

  private buildAnalyticsItems(seats: Seat[]) {
    const gameId = String(this.adobeDataLayerService.getEventId() ?? '');
    const matchup = this.adobeDataLayerService.getMatchupFull() ?? '';
    return buildCartItems(seats, gameId, matchup);
  }

  /**
   * Updates the cart service's representation of selected seats grouped by section.
   * It recalculates the `selectedSeatsBySection` map, creates a sorted array
   * `sortedSelectedSeatsBySectionArray`, notifies subscribers of changes, and
   * cleans up any state related to isolated seats or pending selections on the viewer.
   */
  updateCartSeatsBySection() {
    this.cartService.selectedSeatsBySection =
      this.utilitiesService.distributeSeatsBySection(
        Object.values(this.cartService.selectedSeats)
      );

    this.cartService.sortedSelectedSeatsBySectionArray =
      this.utilitiesService.sortSectionsBySeatsQuantity(
        this.cartService.selectedSeatsBySection
      );
    this.cartService.sortedSelectedSeatsBySectionArray$.next(
      this.cartService.sortedSelectedSeatsBySectionArray
    );

    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    // Reset isolated seats state as the cart has been updated.
    this.isolatedSeatsService.isolatedSeatsActive = false;

    this.isolatedSeatsService.lastIsolatedSeats = null;
    this.dvmService.viewerService.removeNodesFromGroup(
      this.dvmService.viewerService.getNodesByGroups('seat', 'pending'),
      'pending'
    );

    // Every add/remove path (seat click, section-row trash, clear-cart, area
    // selection, tutorial) funnels through here once `selectedSeatsBySection`
    // is recomputed.
    this.updateSelectedSectionsOnTopview();
  }

  /**
   * The single reconciler for every "this section has seats in the cart" visual on the
   * top view: the `'selected'` state on section nodes, the `'group1'` red "my seats"
   * fill, and the minimap selection. It always derives from the current
   * `selectedSeatsBySection`, so it applies *and removes* highlights (sections emptied
   * from the cart go back to plain available) — including the empty-cart case, which
   * the callers used to guard away and which left stale highlights behind.
   * Interacts with viewer groups and potentially the minimap service.
   */
  updateSelectedSectionsOnTopview() {
    if (!this.dvmService.isTopView()) {
      return;
    }

    // Get selected sections from cartService
    const selectedSeatsSectionIds = Object.values(
      this.cartService.selectedSeatsBySection
    );
    const nodeList = selectedSeatsSectionIds.map(item => item.id);

    const staleSelected = this.cartSelectedSectionIds.filter(
      sectionId => !nodeList.includes(sectionId)
    );
    if (staleSelected.length) {
      this.dvmService.viewerService.unselect(staleSelected);
    }
    if (nodeList.length) {
      this.dvmService.viewerService.select(nodeList);
    }
    this.cartSelectedSectionIds = nodeList;

    // Update selected sections on Topview
    // Clear previous section highlights (assuming 'group1' is used for this purpose)
    const previouslyHighlightedNodes =
      this.dvmService.viewerService.getNodesByGroups('section', 'group1');
    this.dvmService.viewerService.removeNodesFromGroup(
      previouslyHighlightedNodes,
      'group1'
    );
    if (nodeList.length) {
      this.dvmService.viewerService.addNodesToGroup(nodeList, 'group1');
    }

    nodeList.forEach(node => {
      // Ensure the section nodes are visually marked as available/selectable if needed
      this.dvmService.viewerService.setAvailable('section', node);
    });

    const dvmData = this.configurationService.configuration.dvmData;

    // Update selected sections on Mini Map
    if (dvmData.miniMapEnabled && dvmData.miniMapId) {
      this.dvmService.minimapService.unselectAll();
      if (selectedSeatsSectionIds.length) {
        this.dvmService.minimapService.select(selectedSeatsSectionIds);
      }
    }
  }
}
